import { FormattedMessage } from "react-intl";

import { DeliveryStopPod } from "api/response";
import { formatDate } from "helpers/dateTimeHelper";
import { classNames } from "helpers/commonHelpers";
import {
  getDeliveryStopAddressName,
  isStopPodDelivered,
} from "./formatDeliveryStopOptionLabel";
import { getPodStatusMessageId, mapDeliveredAtToStopStatus } from "./podStatus";

const PROVIDER_TIMESTAMP_FORMAT = "D MMMM, HH:mm";

export type DeliveryStopPodRowProps = Readonly<{
  stop: DeliveryStopPod;
  testId: string;
  onSelect?: (stop: DeliveryStopPod) => void;
}>;

function formatProviderTimestamp(stop: DeliveryStopPod): string {
  if (stop.provider_date && stop.provider_time) {
    return formatDate(`${stop.provider_date} ${stop.provider_time}`, PROVIDER_TIMESTAMP_FORMAT);
  }
  if (stop.provider_timestamp) {
    return formatDate(stop.provider_timestamp, PROVIDER_TIMESTAMP_FORMAT);
  }
  return "-";
}

export default function DeliveryStopPodRow({ stop, testId, onSelect }: DeliveryStopPodRowProps) {
  const podStatus = mapDeliveredAtToStopStatus(stop.delivered_at);
  const isDelivered = isStopPodDelivered(stop);
  const providerTimestamp = formatProviderTimestamp(stop);
  const addressName = getDeliveryStopAddressName(stop);

  return (
    <button
      type="button"
      role="menuitem"
      className={classNames("proof-of-delivery-menu__row", {
        "proof-of-delivery-menu__row--disabled": isDelivered,
      })}
      data-testid={testId}
      disabled={isDelivered}
      aria-disabled={isDelivered}
      onClick={() => {
        if (isDelivered) return;
        onSelect?.(stop);
      }}
    >
      <span className="proof-of-delivery-menu__row-content">
        <span className="proof-of-delivery-menu__row-header">
          <span className="proof-of-delivery-menu__stop-label">
            {stop.location_title || stop.location_name || "-"}
          </span>
          <span
            className={classNames("proof-of-delivery-menu__status", {
              "proof-of-delivery-menu__status--pending": podStatus === "pending",
              "proof-of-delivery-menu__status--delivered": podStatus === "delivered",
            })}
          >
            <FormattedMessage
              id={getPodStatusMessageId(podStatus)}
              defaultMessage={podStatus === "delivered" ? "Delivered" : "No POD yet"}
            />
          </span>
        </span>
        <span className="proof-of-delivery-menu__supporting">
          {addressName && (
            <span className="proof-of-delivery-menu__detail proof-of-delivery-menu__detail--name">{addressName}</span>
          )}
          <span className="proof-of-delivery-menu__detail">{stop.full_address}</span>
          <span className="proof-of-delivery-menu__detail">{providerTimestamp}</span>
        </span>
      </span>
    </button>
  );
}
